import type { Business, Invoice, LineItem } from '@/types';
import type { JewelrySummary, JewelryDetailItem } from '@/types/jewelryInvoice';
import Cookies from 'js-cookie';

// Tokens look like {{path.to.value}} in text content.
// They also drive the data-binding picker in the designer.

export interface TokenDescriptor {
  key: string;
  label: string;
  group:
    | 'business'
    | 'invoice'
    | 'customer'
    | 'agent'
    | 'totals'
    | 'weights'
    | 'rates'
    | 'date'
    | 'items'
    | 'summary'
    | 'receipt';
}

export const AVAILABLE_TOKENS: TokenDescriptor[] = [
  // ── Business ────────────────────────────────────────────────────────────────
  { key: 'business.name', label: 'Company Name', group: 'business' },
  { key: 'business.address', label: 'Company Address', group: 'business' },
  { key: 'business.branchName', label: 'Branch Name', group: 'business' },
  { key: 'business.branchAddress', label: 'Branch Address', group: 'business' },
  { key: 'business.panNumber', label: 'PAN / Tax No.', group: 'business' },
  { key: 'business.phone', label: 'Phone', group: 'business' },
  { key: 'business.email', label: 'Email', group: 'business' },
  { key: 'business.logo', label: 'Logo (image)', group: 'business' },

  // ── Invoice ──────────────────────────────────────────────────────────────────
  { key: 'invoice.number', label: 'Bill Number', group: 'invoice' },
  { key: 'invoice.date', label: 'Invoice Date', group: 'date' },
  { key: 'invoice.dateNepali', label: 'Invoice Date (Nepali)', group: 'date' },
  { key: 'invoice.orderDate', label: 'Order Date', group: 'date' },
  {
    key: 'invoice.orderDateNepali',
    label: 'Order Date (Nepali)',
    group: 'date',
  },
  { key: 'invoice.deliveryDate', label: 'Delivery Date', group: 'date' },
  {
    key: 'invoice.deliveryDateNepali',
    label: 'Delivery Date (Nepali)',
    group: 'date',
  },
  { key: 'invoice.billingTime', label: 'Billing Time', group: 'invoice' },
  {
    key: 'invoice.referenceNumber',
    label: 'Reference Number',
    group: 'invoice',
  },
  { key: 'invoice.referenceId', label: 'Reference ID', group: 'invoice' },
  {
    key: 'invoice.printCountMessage',
    label: 'Print Count Message',
    group: 'invoice',
  },
  { key: 'invoice.salesType', label: 'Sales Type', group: 'invoice' },
  { key: 'invoice.remarks', label: 'Remarks', group: 'invoice' },

  // ── Customer ─────────────────────────────────────────────────────────────────
  { key: 'customer.name', label: 'Customer Name', group: 'customer' },
  { key: 'customer.address', label: 'Customer Address', group: 'customer' },
  { key: 'customer.phone', label: 'Customer Phone', group: 'customer' },
  { key: 'customer.panNumber', label: 'Customer PAN', group: 'customer' },

  // ── Agent ────────────────────────────────────────────────────────────────────
  { key: 'agent.name', label: 'Agent Name', group: 'agent' },

  // ── Totals ───────────────────────────────────────────────────────────────────
  { key: 'totals.subTotal', label: 'Sub Total', group: 'totals' },
  { key: 'totals.discountAmount', label: 'Discount Amount', group: 'totals' },
  { key: 'totals.taxableAmount', label: 'Taxable Amount', group: 'totals' },
  { key: 'totals.vatableAmount', label: 'Vatable Amount', group: 'totals' },
  { key: 'totals.vatAmount', label: 'VAT Amount', group: 'totals' },
  { key: 'totals.vatPercent', label: 'VAT %', group: 'totals' },
  {
    key: 'totals.jewelryTaxAmount',
    label: 'Jewelry Tax Amount',
    group: 'totals',
  },
  { key: 'totals.jewelryTaxPercent', label: 'Jewelry Tax %', group: 'totals' },
  { key: 'totals.netAmount', label: 'Net Amount', group: 'totals' },
  { key: 'totals.metalAmount', label: 'Metal Amount', group: 'totals' },
  {
    key: 'totals.receiveNetAmount',
    label: 'Receive Metal Amount',
    group: 'totals',
  },
  {
    key: 'totals.metalAmountBeforeReceive',
    label: 'Metal Amount (Before Receive)',
    group: 'totals',
  },
  {
    key: 'totals.subTotalBeforeReceive',
    label: 'Sub Total (Before Receive)',
    group: 'totals',
  },
  {
    key: 'totals.netAmountBeforeReceive',
    label: 'Net Amount (Before Receive)',
    group: 'totals',
  },
  {
    key: 'totals.netAmountLessAdjustment',
    label: 'Net Amount (Less Adjustment)',
    group: 'totals',
  },
  {
    key: 'totals.NetAmountWithTax',
    label: 'Net Amount With Tax',
    group: 'totals',
  },
  {
    key: 'totals.adjustmentAmount',
    label: 'Adjustment Amount',
    group: 'totals',
  },
  { key: 'totals.balanceAmount', label: 'Balance Amount', group: 'totals' },
  { key: 'totals.purchaseAmount', label: 'Purchase Amount', group: 'totals' },
  { key: 'totals.voucherAmount', label: 'Voucher Amount', group: 'totals' },
  {
    key: 'totals.makingAmount',
    label: 'Total Making Amount',
    group: 'totals',
  },
  { key: 'totals.extraAmount', label: 'Total Extra Amount', group: 'totals' },
  {
    key: 'totals.netWithWastageMetalAmount',
    label: 'Net w/ Wastage Metal Amount',
    group: 'totals',
  },

  // ── Weights ──────────────────────────────────────────────────────────────────
  { key: 'weights.net', label: 'Net Weight', group: 'weights' },
  {
    key: 'weights.netWithWastage',
    label: 'Net Weight w/ Wastage',
    group: 'weights',
  },
  { key: 'weights.pure', label: 'Pure Weight', group: 'weights' },
  { key: 'weights.diamond', label: 'Diamond Weight', group: 'weights' },
  { key: 'weights.stone', label: 'Stone Weight', group: 'weights' },
  { key: 'weights.diamondPcs', label: 'Diamond Pcs', group: 'weights' },
  { key: 'weights.stonePcs', label: 'Stone Pcs', group: 'weights' },
  { key: 'weights.diamondAmount', label: 'Diamond Amount', group: 'weights' },
  { key: 'weights.stoneAmount', label: 'Stone Amount', group: 'weights' },
  {
    key: 'weights.salesDiamondAmount',
    label: 'Sales Diamond Amount',
    group: 'weights',
  },
  {
    key: 'weights.salesStoneAmount',
    label: 'Sales Stone Amount',
    group: 'weights',
  },
  {
    key: 'weights.receiveGold',
    label: 'Receive Gold Weight',
    group: 'weights',
  },
  {
    key: 'weights.receiveSilver',
    label: 'Receive Silver Weight',
    group: 'weights',
  },
  { key: 'weights.gross', label: 'Gross Weight', group: 'weights' },
  { key: 'weights.wastage', label: 'Wastage Weight', group: 'weights' },
  { key: 'weights.receive', label: 'Receive Weight', group: 'weights' },
  { key: 'weights.final', label: 'Final Weight', group: 'weights' },
  {
    key: 'weights.pureWithWastage',
    label: 'Pure Weight w/ Wastage',
    group: 'weights',
  },

  // ── Rates ────────────────────────────────────────────────────────────────────
  { key: 'rates.gold', label: 'Gold Rate (per gm)', group: 'rates' },
  { key: 'rates.silver', label: 'Silver Rate (per gm)', group: 'rates' },
  { key: 'rates.goldInTola', label: 'Gold Rate (per tola)', group: 'rates' },
  {
    key: 'rates.silverInTola',
    label: 'Silver Rate (per tola)',
    group: 'rates',
  },
  { key: 'rates.goldPerLal', label: 'Gold Rate (per lal)', group: 'rates' },
  {
    key: 'rates.goldPer10Gram',
    label: 'Gold Rate (per 10 gm)',
    group: 'rates',
  },
  { key: 'rates.silverPerLal', label: 'Silver Rate (per lal)', group: 'rates' },
  {
    key: 'rates.silverPer10Gram',
    label: 'Silver Rate (per 10 gm)',
    group: 'rates',
  },
  { key: 'rates.orderGold', label: 'Order Gold Rate (per gm)', group: 'rates' },
  {
    key: 'rates.orderSilver',
    label: 'Order Silver Rate (per gm)',
    group: 'rates',
  },
  {
    key: 'rates.orderGoldInTola',
    label: 'Order Gold Rate (per tola)',
    group: 'rates',
  },
  {
    key: 'rates.orderSilverInTola',
    label: 'Order Silver Rate (per tola)',
    group: 'rates',
  },
  {
    key: 'rates.orderGoldPer10Gram',
    label: 'Order Gold Rate (per 10 gm)',
    group: 'rates',
  },
  {
    key: 'rates.orderSilverPer10Gram',
    label: 'Order Silver Rate (per 10 gm)',
    group: 'rates',
  },

  // ── Item Images ───────────────────────────────────────────────────────────────
  { key: 'items.images', label: 'All Item Images', group: 'items' },
  { key: 'items.image0', label: 'Item Image (by index)', group: 'items' },
  { key: 'documentImage', label: 'KYC Document Images', group: 'customer' },

  // ── Summary (bare keys — identical to data.summaryData field names) ────────────
  // Bind by the exact backend key, e.g. {{netAmount}} → data.summaryData.netAmount.
  { key: 'invoiceDate', label: 'invoiceDate', group: 'summary' },
  { key: 'jewelryTaxLabel', label: 'jewelryTaxLabel', group: 'summary' },
  {
    key: 'invoiceDateInNepali',
    label: 'invoiceDateInNepali',
    group: 'summary',
  },
  { key: 'orderDate', label: 'orderDate', group: 'summary' },
  { key: 'orderDateInNepali', label: 'orderDateInNepali', group: 'summary' },
  { key: 'deliveryDate', label: 'deliveryDate', group: 'summary' },
  {
    key: 'deliveryDateInNepali',
    label: 'deliveryDateInNepali',
    group: 'summary',
  },
  { key: 'billNumber', label: 'billNumber', group: 'summary' },
  { key: 'invoiceTitle', label: 'invoiceTitle', group: 'summary' },
  { key: 'printCountMessage', label: 'printCountMessage', group: 'summary' },
  { key: 'salesType', label: 'salesType', group: 'summary' },
  { key: 'referenceNumber', label: 'referenceNumber', group: 'summary' },
  { key: 'referenceId', label: 'referenceId', group: 'summary' },
  { key: 'billingTime', label: 'billingTime', group: 'summary' },
  { key: 'printTime', label: 'printTime', group: 'summary' },
  { key: 'printBy', label: 'printBy', group: 'summary' },
  { key: 'createBy', label: 'createBy', group: 'summary' },
  { key: 'paymentStatus', label: 'paymentStatus', group: 'summary' },
  { key: 'ledgerName', label: 'ledgerName', group: 'summary' },
  { key: 'ledgerAddress', label: 'ledgerAddress', group: 'summary' },
  { key: 'ledgerMobileNumber', label: 'ledgerMobileNumber', group: 'summary' },
  { key: 'ledgerPanNumber', label: 'ledgerPanNumber', group: 'summary' },
  { key: 'agentName', label: 'agentName', group: 'summary' },
  { key: 'remarks', label: 'remarks', group: 'summary' },
  { key: 'isTaxIncluded', label: 'isTaxIncluded', group: 'summary' },
  { key: 'subTotal', label: 'subTotal', group: 'summary' },
  { key: 'discountAmount', label: 'discountAmount', group: 'summary' },
  { key: 'taxableAmount', label: 'taxableAmount', group: 'summary' },
  { key: 'jewelryTaxAmount', label: 'jewelryTaxAmount', group: 'summary' },
  { key: 'jewelryTaxPercent', label: 'jewelryTaxPercent', group: 'summary' },
  { key: 'vatableAmount', label: 'vatableAmount', group: 'summary' },
  { key: 'vatAmount', label: 'vatAmount', group: 'summary' },
  { key: 'vatPercent', label: 'vatPercent', group: 'summary' },
  { key: 'netAmount', label: 'netAmount', group: 'summary' },
  { key: 'metalAmount', label: 'metalAmount', group: 'summary' },
  { key: 'receiveNetAmount', label: 'receiveNetAmount', group: 'summary' },
  { key: 'receiveGoldWeight', label: 'receiveGoldWeight', group: 'summary' },
  {
    key: 'receiveSilverWeight',
    label: 'receiveSilverWeight',
    group: 'summary',
  },
  { key: 'VoucherAmount', label: 'VoucherAmount', group: 'summary' },
  {
    key: 'metalAmountBeforeReceive',
    label: 'metalAmountBeforeReceive',
    group: 'summary',
  },
  {
    key: 'subTotalBeforeReceive',
    label: 'subTotalBeforeReceive',
    group: 'summary',
  },
  {
    key: 'netAmountBeforeReceive',
    label: 'netAmountBeforeReceive',
    group: 'summary',
  },
  { key: 'purchaseAmount', label: 'purchaseAmount', group: 'summary' },
  { key: 'adjustmentAmount', label: 'adjustmentAmount', group: 'summary' },
  { key: 'balanceAmount', label: 'balanceAmount', group: 'summary' },
  { key: 'totalNetWeight', label: 'totalNetWeight', group: 'summary' },
  {
    key: 'totalNetWithWastage',
    label: 'totalNetWithWastage',
    group: 'summary',
  },
  { key: 'totalPureWeight', label: 'totalPureWeight', group: 'summary' },
  {
    key: 'totalPureWithWastage',
    label: 'totalPureWithWastage',
    group: 'summary',
  },
  { key: 'totalGrossWeight', label: 'totalGrossWeight', group: 'summary' },
  {
    key: 'totalWastageWeight',
    label: 'totalWastageWeight',
    group: 'summary',
  },
  {
    key: 'totalReceiveWeight',
    label: 'totalReceiveWeight',
    group: 'summary',
  },
  { key: 'totalFinalWeight', label: 'totalFinalWeight', group: 'summary' },
  { key: 'totalMakingAmount', label: 'totalMakingAmount', group: 'summary' },
  { key: 'totalExtraAmount', label: 'totalExtraAmount', group: 'summary' },
  {
    key: 'totalNetWithWastageAmount',
    label: 'totalNetWithWastageAmount',
    group: 'summary',
  },
  {
    key: 'totalNetWithWastageMetalAmount',
    label: 'totalNetWithWastageMetalAmount',
    group: 'summary',
  },
  { key: 'totalDiamondWeight', label: 'totalDiamondWeight', group: 'summary' },
  { key: 'totalStoneWeight', label: 'totalStoneWeight', group: 'summary' },
  { key: 'totalDiamondPcs', label: 'totalDiamondPcs', group: 'summary' },
  { key: 'totalStonePcs', label: 'totalStonePcs', group: 'summary' },
  { key: 'totalDiamondAmount', label: 'totalDiamondAmount', group: 'summary' },
  { key: 'totalStoneAmount', label: 'totalStoneAmount', group: 'summary' },
  {
    key: 'totalSalesDiamondAmount',
    label: 'totalSalesDiamondAmount',
    group: 'summary',
  },
  {
    key: 'totalSalesStoneAmount',
    label: 'totalSalesStoneAmount',
    group: 'summary',
  },
  { key: 'billGoldRate', label: 'billGoldRate', group: 'summary' },
  { key: 'billSilverRate', label: 'billSilverRate', group: 'summary' },
  { key: 'goldRateInTola', label: 'goldRateInTola', group: 'summary' },
  { key: 'silverRateInTola', label: 'silverRateInTola', group: 'summary' },
  { key: 'goldRatePerLal', label: 'goldRatePerLal', group: 'summary' },
  { key: 'goldRatePerGram', label: 'goldRatePerGram', group: 'summary' },
  { key: 'goldRatePer10Gram', label: 'goldRatePer10Gram', group: 'summary' },
  { key: 'goldRatePerTola', label: 'goldRatePerTola', group: 'summary' },
  { key: 'silverRatePerLal', label: 'silverRatePerLal', group: 'summary' },
  { key: 'silverRatePerGram', label: 'silverRatePerGram', group: 'summary' },
  {
    key: 'silverRatePer10Gram',
    label: 'silverRatePer10Gram',
    group: 'summary',
  },
  { key: 'silverRatePerTola', label: 'silverRatePerTola', group: 'summary' },
  { key: 'orderGoldRate', label: 'orderGoldRate', group: 'summary' },
  { key: 'orderSilverRate', label: 'orderSilverRate', group: 'summary' },
  {
    key: 'orderGoldRateInTola',
    label: 'orderGoldRateInTola',
    group: 'summary',
  },
  {
    key: 'orderSilverRateInTola',
    label: 'orderSilverRateInTola',
    group: 'summary',
  },
  {
    key: 'orderGoldRatePer10Gram',
    label: 'orderGoldRatePer10Gram',
    group: 'summary',
  },
  {
    key: 'orderSilverRatePer10Gram',
    label: 'orderSilverRatePer10Gram',
    group: 'summary',
  },

  // ── Receipt voucher (first 3 receipt entries with debit > 0) ─────────────────
  {
    key: 'receipt.1-name',
    label: 'Receipt 1 - Account Name',
    group: 'receipt',
  },
  { key: 'receipt.1-amount', label: 'Receipt 1 - Amount', group: 'receipt' },
  { key: 'receipt.1-group', label: 'Receipt 1 - Mode', group: 'receipt' },
  {
    key: 'receipt.2-name',
    label: 'Receipt 2 - Account Name',
    group: 'receipt',
  },
  { key: 'receipt.2-amount', label: 'Receipt 2 - Amount', group: 'receipt' },
  { key: 'receipt.2-group', label: 'Receipt 2 - Mode', group: 'receipt' },
  {
    key: 'receipt.3-name',
    label: 'Receipt 3 - Account Name',
    group: 'receipt',
  },
  { key: 'receipt.3-amount', label: 'Receipt 3 - Amount', group: 'receipt' },
  { key: 'receipt.3-group', label: 'Receipt 3 - Mode', group: 'receipt' },
];
export interface VoucherEntryCtx {
  ledgerName: string;
  debitAmount: number | string;
  creditAmount: number | string;
  remarks?: string;
  groupName?: string;
  raw?: Record<string, unknown>;
}

export interface VoucherCtx {
  voucherType?: string;
  voucherNo?: string;
  voucherDate?: string;
  narration?: string;
  entries: VoucherEntryCtx[];
}

export interface TokenContext {
  business: Business | null;
  invoice: Invoice | null;
  summary: JewelrySummary | null;
  details: JewelryDetailItem[] | null;
  voucher?: VoucherCtx | null;
}

function strVal(v: unknown): string {
  return v != null && v !== '' ? String(v) : '';
}

// Print the value exactly as the backend returned it — no thousands grouping
// and no forced decimal count. A JSON number like 51194.93 prints "51194.93",
// 52000.0 prints "52000". (On-screen grids keep their own formatting; this is
// only the print/token path.)
function numVal(v: unknown): string {
  if (v == null || v === '') return '';
  return String(v);
}

type GroupResolver = (field: string, ctx: TokenContext) => string;

const GROUP_RESOLVERS: Record<string, GroupResolver> = {
  business(field, { business }) {
    if (!business) return '';
    if (field === 'logo') return business.logoDataUrl ?? '';
    return strVal(business[field as keyof Business]);
  },

  invoice(field, { invoice, summary }) {
    if (summary) {
      const map: Record<string, string | null | undefined> = {
        number: summary.billNumber,
        date: summary.invoiceDate,
        dateNepali: summary.invoiceDateInNepali,
        orderDate: summary.orderDate,
        orderDateNepali: summary.orderDateInNepali,
        deliveryDate: summary.deliveryDate,
        deliveryDateNepali: summary.deliveryDateInNepali,
        billingTime: summary.billingTime,
        referenceNumber: summary.referenceNumber,
        referenceId: summary.referenceId,
        printCountMessage: summary.printCountMessage,
        salesType: summary.salesType,
        remarks: summary.remarks,
      };
      return strVal(map[field]);
    }
    if (invoice) return strVal(invoice[field as keyof Invoice]);
    return '';
  },

  // Alias for invoice.date — handles keys with a 'date' prefix if added in future.
  date(field, { summary, invoice }) {
    const v = summary?.invoiceDate ?? invoice?.[field as keyof Invoice];
    return strVal(v);
  },

  customer(field, { invoice, summary }) {
    if (summary) {
      const map: Record<string, string | undefined> = {
        name: summary.ledgerName,
        address: summary.ledgerAddress,
        phone: summary.ledgerMobileNumber,
        panNumber: summary.ledgerPanNumber,
      };
      return strVal(map[field]);
    }
    if (invoice)
      return strVal(invoice.customer?.[field as keyof typeof invoice.customer]);
    return '';
  },

  agent(field, { summary }) {
    if (summary && field === 'name') return summary.agentName ?? '';
    return '';
  },

  totals(field, { invoice, summary }) {
    if (summary) {
      const map: Record<string, number | string> = {
        subTotal: summary.subTotal,
        discountAmount: summary.discountAmount,
        taxableAmount: summary.taxableAmount,
        vatableAmount: summary.vatableAmount,
        vatAmount: summary.vatAmount,
        vatPercent: summary.vatPercent,
        // The backend renamed luxuryTax* → jewelryTax*. Accept either, and keep
        // the old token keys mapped so saved templates still print.
        jewelryTaxAmount: summary.jewelryTaxAmount ?? summary.luxuryTaxAmount,
        jewelryTaxPercent:
          summary.jewelryTaxPercent ?? summary.luxuryTaxPercent,
        luxuryTaxAmount: summary.jewelryTaxAmount ?? summary.luxuryTaxAmount,
        luxuryTaxPercent: summary.jewelryTaxPercent ?? summary.luxuryTaxPercent,
        netAmount: summary.netAmount,
        metalAmount: summary.metalAmount,
        receiveNetAmount: summary.receiveNetAmount,
        metalAmountBeforeReceive: summary.metalAmountBeforeReceive,
        subTotalBeforeReceive: summary.subTotalBeforeReceive,
        netAmountBeforeReceive: summary.netAmountBeforeReceive,
        netAmountLessAdjustment: summary.netAmountLessAdjustment,
        NetAmountWithTax: summary.NetAmountWithTax,
        adjustmentAmount: summary.adjustmentAmount,
        balanceAmount: summary.balanceAmount,
        purchaseAmount: summary.purchaseAmount,
        voucherAmount: summary.VoucherAmount,
        makingAmount: summary.totalMakingAmount ?? '',
        extraAmount: summary.totalExtraAmount ?? '',
        // Backend renamed totalNetWithWastageMetalAmount → totalNetWithWastageAmount.
        netWithWastageMetalAmount:
          summary.totalNetWithWastageAmount ??
          summary.totalNetWithWastageMetalAmount ??
          '',
      };
      return numVal(map[field]);
    }
    if (invoice) {
      const v = invoice.totals?.[field as keyof typeof invoice.totals];
      return strVal(v);
    }
    return '';
  },

  weights(field, { summary }) {
    if (!summary) return '';
    const map: Record<string, string | number> = {
      net: summary.totalNetWeight,
      netWithWastage: summary.totalNetWithWastage,
      pure: summary.totalPureWeight,
      diamond: summary.totalDiamondWeight,
      stone: summary.totalStoneWeight,
      diamondPcs: summary.totalDiamondPcs,
      stonePcs: summary.totalStonePcs,
      diamondAmount: summary.totalDiamondAmount,
      stoneAmount: summary.totalStoneAmount,
      salesDiamondAmount: summary.totalSalesDiamondAmount ?? '',
      salesStoneAmount: summary.totalSalesStoneAmount ?? '',
      receiveGold: summary.receiveGoldWeight,
      receiveSilver: summary.receiveSilverWeight,
      gross: summary.totalGrossWeight ?? '',
      wastage: summary.totalWastageWeight ?? '',
      receive: summary.totalReceiveWeight ?? '',
      final: summary.totalFinalWeight ?? '',
      pureWithWastage: summary.totalPureWithWastage ?? '',
    };
    return strVal(map[field]);
  },

  rates(field, { summary }) {
    if (!summary) return '';
    const map: Record<string, number | undefined> = {
      gold: summary.billGoldRate,
      silver: summary.billSilverRate,
      goldInTola: summary.goldRateInTola,
      silverInTola: summary.silverRateInTola,
      goldPerLal: summary.goldRatePerLal,
      goldPerGram: summary.goldRatePerGram ?? summary.billGoldRate,
      goldPer10Gram: summary.goldRatePer10Gram,
      goldPerTola: summary.goldRatePerTola ?? summary.goldRateInTola,
      silverPerLal: summary.silverRatePerLal,
      silverPerGram: summary.silverRatePerGram ?? summary.billSilverRate,
      silverPer10Gram: summary.silverRatePer10Gram,
      silverPerTola: summary.silverRatePerTola ?? summary.silverRateInTola,
      orderGold: summary.orderGoldRate,
      orderSilver: summary.orderSilverRate,
      orderGoldInTola: summary.orderGoldRateInTola,
      orderSilverInTola: summary.orderSilverRateInTola,
      orderGoldPer10Gram: summary.orderGoldRatePer10Gram,
      orderSilverPer10Gram: summary.orderSilverRatePer10Gram,
    };
    const v = map[field];
    return v != null ? String(v) : '';
  },

  items(field, { details }) {
    if (!field.startsWith('image')) return '';
    const idx = parseInt(field.replace('image', ''), 10);
    const firstItem = details?.[0];
    if (!firstItem) return '';
    const imgEntry = (firstItem.images as any)?.[isNaN(idx) ? 0 : idx];
    if (!imgEntry) return '';
    return typeof imgEntry === 'string' ? imgEntry : (imgEntry?.image ?? '');
  },

  receipt(field, { summary }) {
    if (!summary) return '';
    const [idxStr, prop] = field.split('-');
    const n = parseInt(idxStr, 10);
    if (!n || !prop) return '';
    const rawEntries = (summary as any)?.voucherData?.voucherEntries;
    const entries: any[] = Array.isArray(rawEntries)
      ? rawEntries.filter((e: any) => parseFloat(e?.debitAmount) > 0)
      : [];
    const entry = entries[n - 1];
    if (!entry) return '';
    if (prop === 'name') return strVal(entry.ledgerName);
    if (prop === 'amount') return numVal(entry.debitAmount);
    if (prop === 'group') return strVal(entry.groupName);
    return '';
  },
};

// Bare summary keys that the backend has since renamed. A template saved against
// the old name falls back to the new field instead of printing blank.
const SUMMARY_KEY_ALIASES: Record<string, string> = {
  luxuryTaxAmount: 'jewelryTaxAmount',
  luxuryTaxPercent: 'jewelryTaxPercent',
};

// Resolve a single token to a string.
// When summary data is available it takes priority over the generic Invoice type.
// Returns empty string in designer preview mode (no ctx data available).
export function resolveToken(key: string, ctx: TokenContext): string {
  const dotIdx = key.indexOf('.');

  // Bare key (no dot) — bind directly to a backend summaryData field of the
  // same name, so the bind key equals the response key 1:1 ({{netAmount}}).
  if (dotIdx === -1) {
    if (!ctx.summary) return '';
    const record = ctx.summary as unknown as Record<string, unknown>;
    let v = record[key];
    if (v === undefined && SUMMARY_KEY_ALIASES[key]) {
      v = record[SUMMARY_KEY_ALIASES[key]];
    }
    // No matching data → print blank rather than echoing the raw {{token}}.
    if (v === undefined) return '';
    return typeof v === 'number' ? String(v) : strVal(v);
  }

  const group = key.slice(0, dotIdx);
  const field = key.slice(dotIdx + 1);

  // Unknown token group → print blank rather than echoing the raw {{token}}.
  if (!GROUP_RESOLVERS[group]) return '';
  if (!(ctx.business || ctx.invoice || ctx.summary)) return '';

  return GROUP_RESOLVERS[group](field, ctx);
}

// Replace all {{tokens}} in a string with their resolved values.
export function resolveText(text: string, ctx: TokenContext): string {
  return text.replace(/\{\{([^}]+)\}\}/g, (_m, key) =>
    resolveToken(key.trim(), ctx),
  );
}

// Alias map: if a column key returns empty/null on a JewelryDetailItem, try
// this alternate field name automatically. Keeps old templates working without
// redesign. LineItem templates won't be affected because their primary key
// fields (description, quantity, etc.) are non-empty.
const JEWELRY_KEY_ALIASES: Record<string, string> = {
  description: 'itemName',
  name: 'itemName',
  // Backend renamed luxuryTax* → jewelryTax*; keep old column keys working.
  luxuryTaxAmount: 'jewelryTaxAmount',
  luxuryTaxPercent: 'jewelryTaxPercent',
  // Backend renamed netWithWastageMetalAmount → netWithWastageAmount. Alias both
  // ways so a template prints against either response shape.
  netWithWastageMetalAmount: 'netWithWastageAmount',
  netWithWastageAmount: 'netWithWastageMetalAmount',
  taxPercent: 'vatPercent',
  tax: 'vatPercent',
  discountPercent: 'discountAmount',
  pureWt: 'pureWeight',
  netWt: 'netWeight',
  grossWt: 'grossWeight',
  itemTotal: 'netAmount',
  total: 'netAmount',
  amount: 'netAmount',
};

// For tables, resolve a column's value for a single line item.
// Pass `rowIndex` (0-based) to support the special "sn" (serial number) key.
const GRAM_KEYS = new Set(['grossWeight', 'wastageWeight', 'receiveWeight']);

export function resolveLineItemCell(
  key: string,
  item: LineItem | JewelryDetailItem,
  format: 'text' | 'number' | 'currency' | 'percent' = 'text',
  rowIndex = 0,
  _currencyPrefix = '',
): string {
  if (key === 'sn' || key === '#') return String(rowIndex + 1);

  const record = item as unknown as Record<string, unknown>;
  let raw = record[key];

  if (
    (raw === undefined || raw === null || raw === '') &&
    JEWELRY_KEY_ALIASES[key]
  ) {
    raw = record[JEWELRY_KEY_ALIASES[key]];
  }

  if (raw === undefined || raw === null || raw === '') return '';

  if (GRAM_KEYS.has(key)) {
    const n = Number(raw);
    // const unit = Cookies.get('primaryCalculationType');
    return isNaN(n) ? String(raw) : `${n}`;
  }

  // number/currency columns print the backend value verbatim — no thousands
  // grouping, no forced decimals, and no currency prefix. (currencyPrefix is
  // intentionally ignored here so the printed amount equals the response.)
  if (format === 'number' || format === 'currency') {
    return String(raw);
  }
  if (format === 'percent') return `${Number(raw)}%`;
  return String(raw);
}
